import {
  FluxAccountHolder,
  FluxAccountType,
  FluxPartyType,
  FluxUtilityType,
  PaymentMethodType,
  SignupSteps,
  State,
  StreetType,
} from '@energy-retailer/constants';
import { create } from 'zustand';

import type { SignupBillingCallbackType } from '@energy-retailer/constants';
import type {
  CompleteBillingSchema,
  DetailsSchema,
  ParsedWindcaveCreateSessionSchema,
  SignupSchema,
} from '@energy-retailer/schema';

export const STEP_DETAILS = [
  {
    name: SignupSteps.Details,
    title: 'Details',
    number: '1',
    url: '/sign-up/details',
  },
  {
    name: SignupSteps.Billing,
    title: 'Billing',
    number: '2',
    url: '/sign-up/billing',
  },
  {
    name: SignupSteps.Review,
    title: 'Review',
    number: '3',
    url: '/sign-up/review',
  },
];

type SignUpFlowStore = SignupSchema & {
  billingSession: ParsedWindcaveCreateSessionSchema;
};

type SignUpFlowActions = {
  reset: () => void;
  resetBillingTerms: () => void;
  setDetails: (details: DetailsSchema) => void;
  setBilling: (billing: CompleteBillingSchema) => void;
  setCompletedSteps: (steps: SignupSteps[]) => void;
  setBillingSession: (
    billingSession: ParsedWindcaveCreateSessionSchema
  ) => void;
};

const initialState: SignUpFlowStore = {
  details: {
    address: {
      full: '',
      suburb: '',
      postcode: '',
      region: State.VIC,
      house_number: undefined,
      country: '',
      street_name: '',
      street_type: StreetType.Street,
    },

    accountType: FluxAccountType.Residential,
    parties: {
      partyType: FluxPartyType.Person,
      partyPerson: {
        title: FluxAccountHolder.DR,
        givenName: '',
        familyName: '',
        telephone: '',
        birthdate: '',
        email: '',
        reTypyEmail: '',
      },
    },

    connection: {
      electricityConnection: {
        utilityType: FluxUtilityType.Electricity,
        externalId: '123',
      },
    },
    connectionInformation: {
      currentSituation: undefined,
      isExistingMember: undefined,
      requestedSupplyDate: '',
      isEvEligibleCustomer: false,
      ownElectricVehicle: false,
    },
    hazards: {
      hasHazard: undefined,
      hazards: [],
      otherNote: '',
    },
    helpToGetConnected: true,
    sensitiveLoad: undefined as unknown as DetailsSchema['sensitiveLoad'],
    products: {
      electricityFluxId: '',
    },
  },
  billing: {
    paymentType: PaymentMethodType.CreditDebitCard,
    paymentResult: '' as SignupBillingCallbackType,
    paymentSessionId: '',
    windcavePaymentType: '',
    cardType: '',
    cardHolderName: '',
    truncatedCardNumber: '',
    token: '',
    gatewayKey: '',
    termsAndConditionsAcceptedAt: new Date(),
    preferred: false,
    expiryDate: { month: '', year: '' },
    terms_and_conditions: false,
    // accountName: '',
    // bsb: '',
    // accountNumber: '',
  },
  completedSteps: [],
  billingSession: {
    id: '',
    href: '',
    expiryDate: '',
  },
};

export const useSignUpFlowStore = create<SignUpFlowStore & SignUpFlowActions>()(
  (set, state) => ({
    ...initialState,
    setDetails: (details: DetailsSchema) => {
      set({ details });
    },
    setBilling: (billing: CompleteBillingSchema) => {
      set({ completedSteps: [SignupSteps.Details, SignupSteps.Billing] });
      set({ billing });
    },
    setBillingSession: (billingSession: ParsedWindcaveCreateSessionSchema) => {
      set({ billingSession });
    },
    setCompletedSteps: (steps: SignupSteps[]) => {
      set({ completedSteps: steps });
    },
    resetBillingTerms: () => {
      const current: SignUpFlowStore = state();
      if (current.billing.paymentType === PaymentMethodType.DirectDebit) {
        set({
          ...current,
          billing: {
            ...current.billing,
            terms_and_conditions: false,
            read_terms: false,
          },
        });
      }
    },
    reset: () => {
      set(initialState);
    },
  })
);
